import React from 'react';
import { DropDown, ErrorMessage, TooltipWidget } from 'anf-core-react';
import {
  TmntText, PhoneNumberField, ControlledInputField,
  ControlledBirthdayFields,
} from '../../Common';
import FieldTitle from '../../FieldTitle/FieldTitle';
import { processGenderList } from '../../MyInfo/helpers';

/**
 * Renders a controlled last name input field
 * @param {Object} field - Field configuration with name, label, and error properties
 * @param {Object} control - React Hook Form control object
 * @returns {JSX.Element} Last name input field with validation
 */
const renderNameField = (field, control) => {
  const { name, label, error } = field;
  const id = `join-now-${name}`;

  return (
    <ControlledInputField
      autoComplete="name"
      id={id}
      key={id}
      label={label?.value}
      name={name}
      control={control}
    >
      <ErrorMessage>
        <TmntText tmnt={error} />
      </ErrorMessage>
    </ControlledInputField>
  );
};

/**
 * Renders a phone number field with interactive tooltip
 * @param {Object} field - Field configuration with name, label, and error properties
 * @param {Object} control - React Hook Form control object
 * @param {Object} context - Additional context for phone field rendering
 * @param {Object} context.textFor - Localized text content for tooltip
 * @param {Object} context.config - Configuration object with phone number settings
 * @param {boolean} context.isTooltipOpen - Current tooltip visibility state
 * @param {Function} context.setIsToolTipOpen - Function to toggle tooltip visibility
 * @returns {JSX.Element} Phone field with validation and informational tooltip
 */
const renderPhoneField = (
  field,
  control,
  {
    textFor, config, isTooltipOpen, setIsToolTipOpen,
  },
) => {
  const { name, label, error } = field;
  const handleOnClick = () => setIsToolTipOpen(!isTooltipOpen);

  const handleOnKepUp = (event) => {
    event.stopPropagation();
    setIsToolTipOpen(event.key !== 'Escape');
  };

  return (
    <div className="join-now-phone-number-input-field" key={`join-now-${name}`}>
      <PhoneNumberField
        control={control}
        id={`join-now-${name}`}
        label={label?.value}
        name={name}
      >
        <TooltipWidget
          buttonLabelText={textFor?.joinNowPhoneNumberTooltipLabel.value}
          contentPosition="top-right"
          contentID="keep-me-signed-in-tooltip"
          isOpen={isTooltipOpen}
          onClick={handleOnClick}
          onKeyUp={handleOnKepUp}
        >
          <p>
            <TmntText tmnt={textFor?.joinNowTooltipWhyPhoneNumberText} classNames="tooltip-why-text" />
          </p>
          <p>
            <TmntText tmnt={textFor?.joinNowTooltipCantRememberText} />
          </p>
          <>
            <TmntText tmnt={textFor?.joinNowTooltipMoreQuestionsText} />
            <span>
              {config?.toolTipCustomerServicePhoneNumber}
            </span>
          </>
        </TooltipWidget>
        <ErrorMessage>
          <TmntText tmnt={error} />
        </ErrorMessage>
      </PhoneNumberField>
      <div className="phone-number-country-code-instructions">
        <TmntText tmnt={textFor?.countryCodeInstructionsText} />
      </div>
    </div>
  );
};

/**
 * Renders birthday input fields with validation
 * @param {Object} control - React Hook Form control object
 * @param {Object} context - Context object with form utilities and text
 * @param {Object} context.textFor - Localized text content including birthday field labels
 * @param {Function} context.clearErrors - Function to clear form errors
 * @param {Function} context.setError - Function to set form errors
 * @param {Function} context.getValues - Function to get current form values
 * @returns {JSX.Element|null} Birthday fields component or null if not configured
 */
const renderBirthdayFields = (
  control,
  {
    textFor, clearErrors, setError, getValues,
  },
) => {
  if (!textFor?.birthday?.value) return null;

  return (
    <ControlledBirthdayFields
      id="join-form-birthday"
      name="birthdayFields"
      control={control}
      clearErrors={clearErrors}
      setError={setError}
      getValues={getValues}
      title={textFor?.birthday}
      optionalText={textFor?.optional || {}}
    />
  );
};

/**
 * Renders shopping preferences dropdown (gender selection)
 * @param {Object} props - Props object containing text and field data
 * @param {Object} props.textFor - Localized text content including preference options
 * @param {Object} props.genderField - React Hook Form field object for gender selection
 * @returns {JSX.Element|null} Gender preferences dropdown or null if no options configured
 */
const renderPreferences = (
  { textFor, genderField },
) => {
  if (!textFor?.shoppingPreferenceText?.options?.length) return null;

  return (
    <>
      <FieldTitle
        title={textFor?.shoppingPreferenceLabel?.value}
        titleTextKey={textFor?.shoppingPreferenceLabel?.key}
        optionalText={textFor?.optional?.value || {}}
      />
      <DropDown
        id="join-form-gender"
        key="join-form-gender"
        label={textFor?.shoppingPreferenceText?.label?.value}
        name="gender"
        value={genderField.value || ''}
        onChange={(e) => { genderField.onChange(e); }}
        options={(textFor?.shoppingPreferenceText?.options || []).map(processGenderList)}
      />
    </>
  );
};

/**
 * Field Registry - Mapping of field names to their respective renderer functions
 *
 * This registry enables dynamic field rendering based on field configuration.
 * Each key corresponds to a field name, and the value is the rendering function
 * responsible for that field type.
 *
 * Usage: fields['first-name'](fieldConfig, control, context)
 *
 * @type {Object.<string, Function>}
 */
const fields = {
  'first-name': renderNameField,
  'last-name': renderNameField,
  phone: renderPhoneField,
  birthday: renderBirthdayFields,
  preferences: renderPreferences,
};

export default fields;
